// Shared column-filter pieces for the admin tables.
//
// These were defined inside OrdersTable.tsx and not exported. ATAD-132 added a
// second admin table that wants the same dropdown, and having it import from
// OrdersTable would have pointed the dependency the wrong way round.
//
// Deliberately not in helpers/reactTable.tsx: that module is pure cell
// formatters, and this is a stateful component with a click-outside effect —
// filing it there would drag react-icons and three hooks into the import graph
// of every module that only wants a date formatter.

import { useState, useEffect, useMemo, useRef } from 'react';
import { FaFilter } from 'react-icons/fa';

// Multi-select checkbox filter component
export function MultiSelectColumnFilter({
  column: { filterValue = [], setFilter, preFilteredRows, id },
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  const options = useMemo(() => {
    const optionsSet = new Set();
    preFilteredRows.forEach((row) => {
      const value = row.values[id];
      if (value !== undefined && value !== null && value !== '') {
        optionsSet.add(value);
      }
    });
    return [...optionsSet].sort();
  }, [id, preFilteredRows]);

  const handleCheckboxChange = (option) => {
    const newFilterValue = filterValue.includes(option)
      ? filterValue.filter((val) => val !== option)
      : [...filterValue, option];

    setFilter(newFilterValue.length ? newFilterValue : undefined);
  };

  const clearAll = () => {
    setFilter(undefined);
  };

  const selectAll = () => {
    setFilter(options);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-1 px-1 py-0.5 text-xs border rounded hover:bg-gray-100"
      >
        <FaFilter size={10} />
        <span>{filterValue.length > 0 ? `(${filterValue.length})` : ''}</span>
      </button>

      {isOpen && (
        <div className="absolute top-6 left-0 z-50 bg-white border border-gray-300 rounded shadow-lg min-w-40 max-h-48 overflow-y-auto">
          <div className="p-1 border-b bg-gray-50">
            <div className="flex space-x-2">
              <button
                onClick={selectAll}
                className="text-xs text-blue-600 hover:underline"
              >
                All
              </button>
              <button
                onClick={clearAll}
                className="text-xs text-red-600 hover:underline"
              >
                Clear
              </button>
            </div>
          </div>
          <div className="p-1">
            {options.map((option) => (
              <label key={option} className="flex items-center space-x-1 py-0.5 hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={filterValue.includes(option)}
                  onChange={() => handleCheckboxChange(option)}
                  className="rounded text-xs"
                />
                <span className="text-xs">{option}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Custom filter function for multi-select
export function multiSelectFilter(rows, id, filterValue) {
  if (!filterValue || filterValue.length === 0) {
    return rows;
  }

  return rows.filter((row) => {
    const rowValue = row.values[id];
    return filterValue.includes(rowValue);
  });
}
